import { Component } from '@theme/component';

const CM_TO_INCH = 2.54;
const STORAGE_KEY = 'size-guide-unit';
const TOOLTIP_GAP = 4;

/**
 * Owns what theme-drawer doesn't already provide: the cm/in unit toggle(s),
 * positioning for the per-column tooltips, and Escape-dismissal for them.
 * Opening/closing the drawer itself is handled entirely by Horizon's own
 * `on:click="#id/toggle"` declarative wiring on the trigger button — this
 * component never touches dialog open/close state.
 *
 * Unit-toggle radios are wired with a direct `change` listener here (rather
 * than the shared `on:change` declarative binding) since this inline content
 * renders inside variant-picker's own <form>, and the change event still
 * needs `stopPropagation()` regardless — a plain listener keeps that in one
 * place instead of relying on the document-wide delegated dispatch.
 *
 * A drawer can contain more than one independent toggle+table pairing (e.g.
 * a "Belt Sizing" table and a separate "Wraps & Poncho" table, each with its
 * own cm/in control) — every `[data-unit-scope]` container is toggled and
 * initialised independently, scoped to its own cells and radio group, while
 * still sharing one persisted preference so they all start in sync.
 *
 * @extends {Component}
 */
class SizeGuideComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#onChange);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('pointerenter', this.#onTooltipShow, true); // capture: pointerenter doesn't bubble
    this.addEventListener('focusin', this.#onTooltipShow);
    this.#applyStoredUnit();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('pointerenter', this.#onTooltipShow, true);
    this.removeEventListener('focusin', this.#onTooltipShow);
  }

  /**
   * Re-applies the persisted unit after the Section Rendering API morphs this
   * component's subtree (e.g. a variant swap re-renders the inline size-guide-content
   * embedded in variant-picker's own <form>) — connectedCallback doesn't re-run on a
   * morph, so without this the table would silently revert to raw cm text and the
   * radio would reset to "cm" while localStorage still holds the shopper's "in" choice.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#applyStoredUnit();
  }

  #applyStoredUnit() {
    const unit = window.localStorage.getItem(STORAGE_KEY) === 'in' ? 'in' : 'cm';
    this.querySelectorAll('[data-unit-scope]').forEach((scope) => {
      this.#applyUnit(/** @type {HTMLElement} */ (scope), unit);
    });
  }

  /**
   * Positions a column tooltip's popover (size-guide-tooltip.liquid) just before it
   * becomes visible via CSS :hover/:focus-within. It has to be `position: fixed` with
   * JS-computed coordinates rather than plain `position: absolute` + CSS insets: the
   * header cell lives inside `.size-guide__table-wrapper`, which needs
   * `overflow-x: auto` so a wide table can scroll sideways instead of the whole drawer,
   * and per the CSS rule that one non-visible overflow axis forces the other to behave
   * as non-visible too, that wrapper would otherwise clip the popover the moment it
   * tried to escape upward.
   *
   * Clamped to the table's own left/right edges (not just the viewport) so the popover
   * never overflows past the table, and the arrow is shifted to compensate so it still
   * points at the icon even when the popover itself has been shifted off-centre.
   * @param {Event} event
   */
  #onTooltipShow = (event) => {
    const trigger = /** @type {HTMLElement} */ (event.target)?.closest?.('.size-guide__tooltip-trigger');
    if (!trigger) return;

    const popover = trigger.closest('.size-guide__tooltip')?.querySelector('.size-guide__tooltip-popover');
    const th = trigger.closest('th');
    const wrapper = trigger.closest('.size-guide__table-wrapper');
    if (!(popover instanceof HTMLElement) || !(th instanceof HTMLElement) || !(wrapper instanceof HTMLElement)) return;

    const thRect = th.getBoundingClientRect();
    const iconRect = trigger.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    const iconCenter = iconRect.left + iconRect.width / 2;

    const popoverWidth = popover.offsetWidth;
    const minLeft = wrapperRect.left;
    const maxLeft = wrapperRect.right - popoverWidth;
    const left = Math.max(minLeft, Math.min(iconCenter - popoverWidth / 2, maxLeft));

    popover.style.left = `${left}px`;
    popover.style.bottom = `${window.innerHeight - thRect.top + TOOLTIP_GAP}px`;

    const arrowLeft = Math.max(12, Math.min(iconCenter - left, popoverWidth - 12));
    popover.style.setProperty('--_arrow-left', `${arrowLeft}px`);
  };

  /**
   * Switches the displayed unit for the scope the changed radio belongs to,
   * and persists the choice as the default for scopes initialised later.
   * @param {Event} event - The unit-toggle radio's change event.
   */
  #onChange = (event) => {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || radio.type !== 'radio') return;

    const scope = radio.closest('[data-unit-scope]');
    if (!(scope instanceof HTMLElement)) return;

    // Inline size-guide-content is rendered inside variant-picker's own <form> (next to
    // the Size option), so this radio's `change` event would otherwise bubble into
    // variant-picker's document-wide change listener, which treats it as a variant
    // selection and morphs the whole form back to server-rendered (cm) state.
    event.stopPropagation();

    const unit = radio.value === 'in' ? 'in' : 'cm';
    window.localStorage.setItem(STORAGE_KEY, unit);
    this.#applyUnit(scope, unit);
  };

  /**
   * The column tooltips in size-guide-tooltip.liquid are a pure CSS :hover/:focus-within
   * reveal with no close button — dismissed for pointer users the moment they move away,
   * but keyboard users have no "move away" equivalent, so Escape blurs the trigger here to
   * give them the same dismissal (WCAG 1.4.13 Dismissable).
   *
   * Must stop this Escape from also closing the drawer: this listener sits on
   * `<size-guide-component>`, an ancestor of the `<dialog>`, so it's on the dialog's
   * Escape close-watcher's dispatch path — without preventDefault() here, dismissing just
   * the tooltip would also close the whole drawer out from under the shopper.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.closest('.size-guide__tooltip')) {
      event.preventDefault();
      event.stopPropagation();
      active.blur();
    }
  };

  /**
   * Applies a unit to one scope's measurement cells, checked radio, and live region.
   * @param {HTMLElement} scope
   * @param {'cm' | 'in'} unit
   */
  #applyUnit(scope, unit) {
    const isImperial = unit === 'in';
    const liveRegion = /** @type {HTMLElement | null} */ (scope.querySelector('[data-size-guide-live]'));
    // Translated via size-guide-section.liquid's `general.size_guide.unit_cm` key — the JS module
    // has no access to the theme's translation system, so this can't be a hardcoded literal.
    const cmUnitLabel = liveRegion?.dataset.unitLabelCm ?? 'cm';

    scope.querySelectorAll('[data-cm-value]').forEach((cell) => {
      const dataCell = /** @type {HTMLElement} */ (cell);
      const rawValue = dataCell.dataset.cmValue ?? '';
      if (!isImperial) {
        dataCell.textContent = rawValue;
        return;
      }
      // Rejects anything with trailing non-numeric characters (e.g. a "60-65" range),
      // not just Number.parseFloat's own NaN check — parseFloat happily returns 60 for
      // "60-65", which would silently convert a range to a single wrong measurement.
      if (!/^-?\d+(\.\d+)?$/.test(rawValue)) {
        dataCell.textContent = `${rawValue} ${cmUnitLabel}`;
        return;
      }
      const cm = Number.parseFloat(rawValue);
      dataCell.textContent = (cm / CM_TO_INCH).toFixed(1);
    });

    const radio = /** @type {HTMLInputElement | null} */ (scope.querySelector(`input[type="radio"][value="${unit}"]`));
    if (radio) radio.checked = true;

    if (liveRegion) {
      liveRegion.textContent = isImperial
        ? liveRegion.dataset.announceIn ?? ''
        : liveRegion.dataset.announceCm ?? '';
    }
  }
}

if (!customElements.get('size-guide-component')) {
  customElements.define('size-guide-component', SizeGuideComponent);
}
